import { Preferences } from '@capacitor/preferences';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { getFieldMapping, getListByStrapi, listMap } from "src/api/config/systemSwitch";
import {
  collectionsListName,
  listFields,
  ListFieldsKeys,
  ListFieldsRecordType,
  ListFieldsType,
  myDocumentsListName,
  s3cloudUrl,
  siteUrl,
  versionsByDocumentListName,
} from "../../../api/config";
import {
  myTasksLocationsListName,
  myTasksDepartmentsListName,
  myTasksRegionsListName,
  myTasksDependListName,
  myTasks5WayDependListName,
  myTasksDeviationReportDependListName,
  MTTriggersPageListName,
} from "src/api/config/myTasksLists.config";
import { myTasksIncidentstListName, myTasksIncidentstWithRelationsListName } from "src/api/config/myTasksInc.config";
import { myTasksAuditListName, myTasksAuditWithRelationsListName } from "src/api/config/myTasksAudit.config";
import { myTasks5WayListName, myTasks5WayWithRelationsListName } from "src/api/config/myTasks5Way.config";
import _ from "lodash";
import { FIELDS_TYPES } from "src/api/config/type.config";
import { prepareResponse } from "src/api/jsom/strapi_helpers";
import { GetFiltersType, PopulateFieldDescriptor } from "src/modules/modules.type";
import axios from "axios";
import { strapiCheckUrl, strapiUrl } from "src/modules/users/constants";
import store from "src/store";
import { selectCurrentUser } from "src/modules/users/selector";
import { resolveResourceUrl } from "src/utils/url.utils";
import { transformUrlToNewRoute, USE_POST_THRESHOLD } from "src/constants";
import { isPWA } from "src/utils/mobile";

export const filterPrivateCollections = async <K extends ListFieldsKeys>(
  items: ListFieldsRecordType[K][],
  listName: K,
): Promise<ListFieldsRecordType[K][]> => {
  // if (
  //   !window.location.pathname.includes("/dashboard/1/documents") &&
  //   !window.location.pathname.includes("/dashboard/1/notification")
  // ) {
  //   return items;
  // }

  if (listName !== collectionsListName && listName !== myDocumentsListName && listName !== versionsByDocumentListName) {
    return items;
  }

  const currentUser = selectCurrentUser(store.getState());

  if (!currentUser.id || (currentUser.id && currentUser.isAdmin)) return items;

  const userCollectionIds = new Set<number>([
    ...(Array.isArray(currentUser.isCollectionAdmin) ? currentUser.isCollectionAdmin : []),
    ...(Array.isArray(currentUser.isCollectionEditor) ? currentUser.isCollectionEditor : []),
  ]);

  if (listName === collectionsListName) {
    // Items ARE the collections — check isPrivate directly to avoid recursive request.
    // Public collections are always visible; private ones require the user's ID.
    const privateCollectionIds = new Set<number>(
      (items as any[]).filter((item) => item.isPrivate).map((item) => item.ID),
    );
    return (items as any[]).filter(
      (item) => !privateCollectionIds.has(item.ID) || userCollectionIds.has(item.ID),
    ) as unknown as ListFieldsRecordType[K][];
  }

  // For documents / versions: fetch the accessible collections list.
  // The collections request itself goes through filterPrivateCollections for collectionsListName
  // (item-based, no further recursive request) so there is no infinite loop.
  const collectionsResponse = await getStrapiItemsRequest({ listName: collectionsListName });
  const accessibleCollectionIds = new Set<number>(collectionsResponse.items.map((c: any) => c.ID));

  return (items as any[]).filter((item) => {
    const collectionId = Number(item.Collection?.get_lookupId?.() || 0);
    if (!collectionId) return true;
    return accessibleCollectionIds.has(collectionId);
  }) as unknown as ListFieldsRecordType[K][];
};

// ID локации "Регион" (MyTasksLocations / mt-locations), которую нужно скрывать
// из фильтров, справочников и карточек "Мои задачи" для всех, кроме администраторов.
const RESTRICTED_MY_TASKS_LOCATION_ID = 61;

// Списки, где Location лежит прямо на элементе (карточки/зависимости)
const myTasksDirectLocationListNames = new Set<string>([
  myTasksIncidentstListName,
  myTasksIncidentstWithRelationsListName,
  myTasksAuditListName,
  myTasksAuditWithRelationsListName,
  myTasks5WayListName,
  myTasks5WayWithRelationsListName,
  myTasksDependListName,
  myTasks5WayDependListName,
  myTasksDeviationReportDependListName,
  MTTriggersPageListName,
]);

// Таблицы зависимостей Location -> Departament/Department -> Region,
// используемые, чтобы понять, какие отделы/участки относятся к скрытой локации.
const myTasksDependListNamesForLookup = [
  myTasksDependListName,
  myTasks5WayDependListName,
  myTasksDeviationReportDependListName,
  MTTriggersPageListName,
];

const myTasksLocationBasedListNames = new Set<string>([
  myTasksLocationsListName,
  myTasksDepartmentsListName,
  myTasksRegionsListName,
  ...myTasksDirectLocationListNames,
]);

const getLookupIdSafe = (value: any): number => Number(value?.get_lookupId?.() || 0);

/**
 * Скрывает локацию "Регион" (и связанные с ней отделы/участки, инциденты, аудиты,
 * 5П/ОоО, триггеры и т.д.) для всех пользователей, кроме администраторов (isAdmin).
 * Не влияет на списки, не связанные с системой "Мои задачи".
 */
export const filterHiddenMyTasksLocationData = async <K extends ListFieldsKeys>(
  items: ListFieldsRecordType[K][],
  listName: K,
  skipHiddenLocationFilter?: boolean,
): Promise<ListFieldsRecordType[K][]> => {
  if (skipHiddenLocationFilter) return items;
  if (!myTasksLocationBasedListNames.has(listName as unknown as string)) return items;

  const currentUser = selectCurrentUser(store.getState());
  if (currentUser.isAdmin) return items;

  // Сама справочная локация "Регион"
  if (listName === myTasksLocationsListName) {
    return (items as any[]).filter(
      (item) => Number(item.ID) !== RESTRICTED_MY_TASKS_LOCATION_ID,
    ) as unknown as ListFieldsRecordType[K][];
  }

  // Карточки/зависимости, где Location указан напрямую на элементе
  if (myTasksDirectLocationListNames.has(listName as unknown as string)) {
    return (items as any[]).filter(
      (item) => getLookupIdSafe(item.Location) !== RESTRICTED_MY_TASKS_LOCATION_ID,
    ) as unknown as ListFieldsRecordType[K][];
  }

  // Справочники отделов/участков — скрываем те, что привязаны к "Регион"
  // через таблицы зависимостей.
  if (listName === myTasksDepartmentsListName || listName === myTasksRegionsListName) {
    const dependResults = await Promise.all(
      myTasksDependListNamesForLookup.map((dependListName) =>
        getStrapiItemsRequest({
          listName: dependListName as any,
          rowLimit: 5000,
          skipHiddenLocationFilter: true,
        } as any).catch(() => ({ items: [] as any[] })),
      ),
    );

    const hiddenIds = new Set<number>();
    dependResults.forEach((res: any) => {
      (res.items as any[]).forEach((row) => {
        if (getLookupIdSafe(row.Location) === RESTRICTED_MY_TASKS_LOCATION_ID) {
          const deptId = getLookupIdSafe(row.Departament ?? row.Department);
          const regionId = getLookupIdSafe(row.Region);
          if (deptId) hiddenIds.add(deptId);
          if (regionId) hiddenIds.add(regionId);
        }
      });
    });

    return (items as any[]).filter(
      (item) => !hiddenIds.has(Number(item.ID)),
    ) as unknown as ListFieldsRecordType[K][];
  }

  return items;
};

export type { PopulateFieldDescriptor } from "src/modules/modules.type";


// Конфигурация Strapi
const STRAPI_CONFIG = {
  apiUrl: strapiUrl,
  token: "",
};

// Типы для Strapi ответов
interface StrapiResponse<T> {
  data: T[];
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

interface StrapiItem {
  id: number;
  attributes: Record<string, any>;
}

const findSharePointListKey = (strapiListName: string) => {
  return Object.keys(listMap).find((key) => listMap[key as keyof typeof listMap] === strapiListName) ?? "";
};

const normalizeFieldName = (field: string | number | symbol | undefined | null) => {
  if (field === undefined || field === null) {
    return "";
  }
  if (typeof field === "string") {
    return field;
  }
  return field.toString();
};

const createFieldMapper = (strapiListName: string) => {
  const spListKey = findSharePointListKey(strapiListName);
  const fieldMapping = spListKey ? getFieldMapping(spListKey, true) : null;

  return (field: string | number | symbol | undefined | null) => {
    const normalized = normalizeFieldName(field);
    if (!normalized) {
      return "";
    }

    if (fieldMapping && fieldMapping[normalized]) {
      return fieldMapping[normalized];
    }

    return normalized;
  };
};

export type FetchOptions<K extends ListFieldsKeys> = {
  listName: K;
  filters?: Partial<GetFiltersType<K>>;
  filtersType?: "or" | "and";
  // withCredentials?: boolean;
  viewFields?: Partial<Array<keyof ListFieldsType[K]>>;
  sortOrder?:
  | string
  | {
    name: string;
    desc?: boolean;
    asc?: boolean;
  }
  | {
    name: string;
    desc?: boolean;
    asc?: boolean;
  }[];
  nextPage?: number | null;
  rowLimit?: number;
  all?: boolean;
  anySite?: string;
  populateFields?: PopulateFieldDescriptor[]; // Поля для загрузки из связанных объектов
  signal?: any;
  switchPopulate?: boolean;
  switchViewFields?: boolean;
  localFilters?: any;
  otherParams?: any;
  method?: string;
  showPrivateCollections?: boolean;
  skipHiddenLocationFilter?: boolean;
  isLocalDate?: boolean
};

function convertFiltersToOr(urlObj: any, filters: Record<string, any>) {
  // 1) Удаляем старый filters — если есть
  [...urlObj.searchParams.keys()].forEach((key) => {
    if (key.startsWith("filters")) {
      urlObj.searchParams.delete(key);
    }
  });

  // 2) Добавляем новые OR-фильтры
  Object.entries(filters).forEach(([field, condition], index) => {
    Object.entries(condition).forEach(([operator, value]) => {
      const isArray = Array.isArray(value);
      const key = `filters[$or][${index}][${field}][${operator}]${isArray ? "[]" : ""}`;

      if (isArray) {
        value.forEach((v) => urlObj.searchParams.append(key, String(v)));
      } else {
        urlObj.searchParams.append(key, String(value));
      }
    });
  });

  return urlObj.toString();
}

/**
 * Функция для сериализации Strapi фильтров в URL параметры
 * Обрабатывает сложные структуры фильтров включая:
 * - $or и $and операторы
 * - Вложенные операторы ($eq, $contains, $startsWith, $in, etc.)
 * - Простые значения
 *
 * @param filters - объект фильтров от convertFiltersToStrapi
 * @param url - URL объект для добавления параметров
 */

const serializeStrapiFilters = (filters: Record<string, any>, url: URL) => {
  if (!filters || Object.keys(filters).length === 0) {
    return;
  }

  Object.entries(filters).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      return;
    }

    // Обработка специальных операторов ($or, $and)
    if (key === "$or" || key === "$and") {
      if (Array.isArray(value)) {
        value.forEach((condition, index) => {
          if (typeof condition === "object" && condition !== null) {
            Object.entries(condition).forEach(([fieldKey, fieldValue]) => {
              if (typeof fieldValue === "object" && fieldValue !== null) {
                Object.entries(fieldValue).forEach(([operator, operatorValue]) => {
                  url.searchParams.set(
                    `filters[${key}][${index}][${fieldKey}][${operator}]`,
                    operatorValue === null ? "null" : operatorValue.toString(),
                  );
                });
              } else {
                url.searchParams.set(
                  `filters[${key}][${index}][${fieldKey}]`,
                  fieldValue === null ? "null" : fieldValue === undefined ? "undefined" : fieldValue.toString(),
                );
              }
            });
          }
        });
      }
      return;
    }

    // Обработка обычных полей с операторами
    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      Object.entries(value).forEach(([operator, operatorValue]) => {
        if (operatorValue !== null && operatorValue !== undefined) {
          // Специальная обработка для $or и $and внутри поля
          if (operator === "$or" || operator === "$and") {
            if (Array.isArray(operatorValue)) {
              operatorValue.forEach((condition, index) => {
                if (typeof condition === "object" && condition !== null) {
                  Object.entries(condition).forEach(([nestedOperator, nestedValue]) => {
                    // Специальная обработка для дат в $or/$and
                    let serializedNestedValue: string;
                    if (nestedValue instanceof Date) {
                      serializedNestedValue = nestedValue.toISOString();
                    } else {
                      serializedNestedValue =
                        nestedValue === null
                          ? "null"
                          : nestedValue === undefined
                            ? "undefined"
                            : nestedValue.toString();
                    }

                    url.searchParams.set(
                      `filters[${key}][${operator}][${index}][${nestedOperator}]`,
                      serializedNestedValue,
                    );
                  });
                } else {
                  url.searchParams.set(
                    `filters[${key}][${operator}][${index}]`,
                    condition === null ? "null" : condition.toString(),
                  );
                }
              });
            }
          } else {
            // Специальная обработка для $in с массивом
            if (operator === "$in" && Array.isArray(operatorValue)) {
              operatorValue.forEach((item) => {
                let serializedItem: string;
                if (item instanceof Date) {
                  serializedItem = item.toISOString();
                } else {
                  serializedItem = item === null ? "null" : item.toString();
                }

                url.searchParams.append(`filters[${key}][${operator}][]`, serializedItem);
              });
            } else {
              // Специальная обработка для дат - конвертируем в ISO формат
              let serializedValue: string;
              if (operatorValue instanceof Date) {
                serializedValue = operatorValue.toISOString();
              } else {
                serializedValue = operatorValue === null ? "null" : operatorValue.toString();
              }

              url.searchParams.set(`filters[${key}][${operator}]`, serializedValue);
            }
          }
        }
      });
    } else {
      // Простое значение - с обработкой дат
      let serializedValue: string;
      if (value instanceof Date) {
        serializedValue = value.toISOString();
      } else {
        serializedValue = value === null ? "null" : value.toString();
      }

      url.searchParams.set(`filters[${key}]`, serializedValue);
    }
  });
};

// Преобразование SharePoint фильтров в Strapi фильтры
const convertFiltersToStrapi = <K extends ListFieldsKeys>(
  filters: Partial<GetFiltersType<K>> | undefined,
  listName: K,
) => {
  const strapiFilters: Record<string, any> = {};

  if (!filters || Object.keys(filters).length === 0) {
    return strapiFilters;
  }

  const fields = listFields[listName];

  Object.keys(filters).forEach((fieldKey) => {
    const field = fieldKey as keyof ListFieldsType[ListFieldsKeys];
    const value = filters[field];

    if (value === undefined) {
      return;
    }

    if (fieldKey === "$or" && Array.isArray(value)) {
      const orConditions = value.map((condition: any) => convertFiltersToStrapi(condition as any, listName));
      strapiFilters.$or = [...(strapiFilters.$or || []), ...orConditions];
      return;
    }

    const switchField = fields[field as keyof typeof fields];

    // Получаем маппинг полей SharePoint -> Strapi
    const fieldMapping = getFieldMapping(listName, true); // false = SharePoint -> Strapi
    let strapiFieldKey = (fieldMapping && fieldMapping[fieldKey]) || fieldKey;

    // Skip fields that are likely to cause errors in Strapi
    if (!strapiFieldKey || strapiFieldKey === "undefined" || strapiFieldKey === "null") {
      return;
    }

    switch (switchField) {
      case FIELDS_TYPES.STRING:
        if (typeof value === "string") {
          if (value.indexOf("^") > -1) {
            // BeginsWith - используем $startsWith
            const containText = value.replace("^", "");
            strapiFilters[strapiFieldKey] = { $startsWith: containText };
          } else if (value.indexOf("*") === -1) {
            // Exact match
            strapiFilters[strapiFieldKey] = { $eq: value };
          } else {
            // Contains - используем $containsi для поиска без учета регистра
            const containText = value.replace("*", "");
            strapiFilters[strapiFieldKey] = { $containsi: containText };
          }
        } else if (_.isArray(value) && value.length > 0) {
          // Массив значений - используем $in или $or
          const conditions = value.map((v) => {
            if (typeof v === "string") {
              if (v.indexOf("^") > -1) {
                return {
                  [strapiFieldKey]: { $startsWith: v.replace("^", "") },
                };
              } else if (v.indexOf("*") === -1) {
                return { [strapiFieldKey]: { $eq: v } };
              } else {
                return { [strapiFieldKey]: { $containsi: v.replace("*", "") } };
              }
            }
            return { [strapiFieldKey]: { $eq: v } };
          });
          strapiFilters.$or = conditions;
        } else if (!value) {
          strapiFilters[strapiFieldKey] = { $null: true };
        }
        break;

      case FIELDS_TYPES.MULTI_STRING:
        if (typeof value === "string") {
          if (value.indexOf("*") === -1) {
            strapiFilters[strapiFieldKey] = { $eq: value };
          } else {
            const containText = value.replace("*", "");
            strapiFilters[strapiFieldKey] = { $containsi: containText };
          }
        } else if (_.isArray(value) && value.length > 0) {
          const conditions = value.map((v) => ({
            [strapiFieldKey]: { $containsi: v.toString() },
          }));
          strapiFilters.$or = conditions;
        }
        break;

      case FIELDS_TYPES.MULTI_STRING_AND: {
        if (Array.isArray(value) && value.length > 0) {
          // Формируем новые условия из массива
          const newConditions = value.map((v: any) => ({
            [strapiFieldKey]: { $containsi: v.toString() },
          }));
          // Обновляем $and
          strapiFilters.$and = [...newConditions];
        }
        break;
      }
      case FIELDS_TYPES.NUMBER:
        if (_.isArray(value) && value.length > 0) {
          strapiFilters[strapiFieldKey] = { $in: value };
        } else {
          if (!value) {
            strapiFilters[strapiFieldKey] = { $eq: null };
          } else {
            strapiFilters[strapiFieldKey] = { $eq: value };
          }
        }
        break;

      case FIELDS_TYPES.ID:
        // Для ID используем специальную логику - всегда преобразуем в 'id'
        const idFieldKey = fieldKey === "ID" ? "id" : strapiFieldKey;
        if (_.isArray(value) && value.length > 0) {
          // Для массивов ID используем $or с отдельными условиями
          const conditions = value.map((id) => ({
            [idFieldKey]: { $eq: id },
          }));
          // Если уже есть $or, объединяем условия
          if (strapiFilters.$or) {
            strapiFilters.$or = [...strapiFilters.$or, ...conditions];
          } else {
            strapiFilters.$or = conditions;
          }
        } else {
          strapiFilters[idFieldKey] = { $eq: value };
        }
        break;

      case FIELDS_TYPES.LOOKUP:
        if (!value) {
          strapiFilters[strapiFieldKey] = { $null: true };
        } else if (typeof value === "object") {
          strapiFilters[strapiFieldKey] = { $in: value };
        } else {
          strapiFilters[strapiFieldKey] = { $eq: value };
        }
        break;

      case FIELDS_TYPES.MULTI_LOOKUP:
        if (Array.isArray(value) && value.length > 0) {
          // Для связанных данных используем $in
          strapiFilters[strapiFieldKey] = { $in: value };
        }
        break;

      case FIELDS_TYPES.MULTI_USER:
        if (Array.isArray(value) && value.length > 0) {
          strapiFilters[strapiFieldKey] = { $in: value };
        }
        break;

      case FIELDS_TYPES.USER:
        if (value) {
          if (_.isArray(value) && value.length > 0) {
            strapiFilters[strapiFieldKey] = { $in: value };
          } else {
            strapiFilters[strapiFieldKey] = { $eq: value };
          }
        } else {
          strapiFilters[strapiFieldKey] = { $null: true };
        }
        break;

      case FIELDS_TYPES.CHOICE:
        if (_.isArray(value)) {
          strapiFilters[strapiFieldKey] = { $in: value };
        } else {
          strapiFilters[strapiFieldKey] = { $eq: value };
        }
        break;

      case FIELDS_TYPES.BOOLEAN:
        if (Array.isArray(value)) {
          const orConditions = value.map((v) =>
            v === null ? { [strapiFieldKey]: { $null: true } } : { [strapiFieldKey]: { $eq: v } },
          );
          strapiFilters.$or = orConditions;
        } else if (value === true) {
          strapiFilters[strapiFieldKey] = { $eq: true };
        } else if (value === false) {
          strapiFilters[strapiFieldKey] = { $eq: false };
        }
        break;

      case FIELDS_TYPES.DATE:
        if (_.isObject(value) && !_.isArray(value)) {
          const dateValue = value as {
            isNotNull?: boolean;
            isNull?: boolean;
            start?: any;
            end?: any;
          };
          if (dateValue.isNotNull || dateValue.isNull) {
            if (dateValue.isNotNull) {
              strapiFilters[strapiFieldKey] = { $notNull: true };
            } else if (dateValue.isNull) {
              strapiFilters[strapiFieldKey] = { $eq: null };
            }
          } else if (dateValue.start || dateValue.end) {
            const dateFilter: any = {};
            if (dateValue.start) {
              dateFilter.$gte = dateValue.start;
            }
            if (dateValue.end) {
              dateFilter.$lte = dateValue.end;
            }
            strapiFilters[strapiFieldKey] = dateFilter;
          }
        } else if (_.isArray(value)) {
          const dateConditions = value
            .map((v: any) => {
              if (v.start && v.end) {
                return {
                  [strapiFieldKey]: {
                    $gte: v.start,
                    $lte: v.end,
                  },
                };
              }
              return null;
            })
            .filter(Boolean);

          if (dateConditions.length > 0) {
            strapiFilters.$or = dateConditions;
          }
        }
        break;

      default:
        // Для неизвестных типов используем простое равенство
        strapiFilters[strapiFieldKey] = { $eq: value };
        break;
    }
  });

  return strapiFilters;
};

/**
 * Функция для преобразования SharePoint полей в Strapi поля для сортировки
 * В Strapi некоторые поля (например, id) находятся на верхнем уровне объекта,
 * а остальные поля находятся в attributes
 *
 * @param fieldName - имя поля в SharePoint формате
 * @param listName - имя списка для получения маппинга
 * @returns имя поля в Strapi формате
 */
const convertSharePointFieldToStrapi = (fieldName: string, listName: string): string => {
  // Получаем маппинг полей SharePoint -> Strapi
  const fieldMapping = getFieldMapping(listName, true); // true = ищем по значению и возвращаем ключ
  if (fieldMapping && fieldMapping[fieldName]) {
    return fieldMapping[fieldName];
  }

  // Специальные случаи для полей, которые находятся на верхнем уровне в Strapi
  if (fieldName === "ID" || fieldName === "id") {
    return "id"; // В Strapi id находится на верхнем уровне
  }

  // Другие возможные поля, которые могут быть на верхнем уровне
  const topLevelFields = ["createdAt", "updatedAt", "publishedAt"];
  if (topLevelFields.includes(fieldName)) {
    return fieldName;
  }

  // Для остальных полей используем как есть (они находятся в attributes)
  return fieldName;
};

// Преобразование сортировки SharePoint в Strapi
const convertSortOrderToStrapi = (sortOrder: FetchOptions<ListFieldsKeys>["sortOrder"], listName: string): string[] => {
  if (!sortOrder) return [];

  if (typeof sortOrder === "string") {
    const fieldName = convertSharePointFieldToStrapi(sortOrder, listName);
    return [fieldName];
  } else if (_.isObject(sortOrder) && !_.isArray(sortOrder)) {
    const direction = sortOrder.desc ? "desc" : "asc";
    const fieldName = convertSharePointFieldToStrapi(sortOrder.name, listName);
    return [`${fieldName}:${direction}`];
  } else if (_.isArray(sortOrder) && sortOrder.length > 0) {
    return sortOrder.map((sort) => {
      const direction = sort.desc ? "desc" : "asc";
      const fieldName = convertSharePointFieldToStrapi(sort.name, listName);
      return `${fieldName}:${direction}`;
    });
  }

  return [];
};

// Тестовая функция для проверки сериализации фильтров
const testFilterSerialization = () => {
  const testFilters = {
    Statuses: { $eq: "active" },
    isPrivate: {
      $or: [{ $eq: false }, { $null: true }],
    },
  };

  const url = new URL("https://example.com/api/test");
  serializeStrapiFilters(testFilters, url);

  console.log("Test URL params:", url.searchParams.toString());
  return url.searchParams.toString();
};

// Определение полей для populate на основе конфигурации списка
const getPopulateFields = (listName: string) => {
  const fields = listFields[listName];
  return "*";

  // const populateFields: string[] = [];

  // Object.values(fields).forEach((field: any) => {
  //   if (
  //     field === STRAPI_FIELDS_TYPES.RELATION ||
  //     field === STRAPI_FIELDS_TYPES.MEDIA ||
  //     field === STRAPI_FIELDS_TYPES.COMPONENT ||
  //     field === STRAPI_FIELDS_TYPES.DYNAMIC_ZONE ||
  //     field === STRAPI_FIELDS_TYPES.LOCALIZATIONS
  //   ) {
  //     populateFields.push(field);
  //   }
  // });

  // return populateFields.length > 0 ? populateFields.join(",") : "*";
};

// Создание URL с параметрами для Strapi
const buildStrapiUrl = (
  listName: string,
  filters?: Record<string, any>,
  sort?: string[],
  page?: number,
  pageSize?: number,
  viewFields?: string[],
  populateFields?: PopulateFieldDescriptor[],
  filtersType?: string,
  switchPopulate: boolean = true,
  switchViewFields: boolean = false,
  localFilters: any = false,
  otherParams: any = false,
) => {
  const url = new URL(`${STRAPI_CONFIG.apiUrl}${listName}`);
  const mapFieldName = createFieldMapper(listName);

  // Поля — только если есть и не пустые. Strapi 4 ожидает массив: fields[0]=title&fields[1]=body
  if (switchViewFields && viewFields && viewFields.length > 0) {
    const validFields = viewFields.filter((field) => field && field.trim() !== "");
    const strapiFields = validFields.map((field) => {
      const fieldMapping = getFieldMapping(listName, false);
      if (fieldMapping && fieldMapping[field]) {
        return fieldMapping[field];
      }
      if (field === "ID" || field === "id") {
        return "id";
      }
      return field;
    });
    strapiFields.forEach((field, index) => {
      url.searchParams.set(`fields[${index}]`, field);
    });
  }

  if (populateFields && populateFields.length > 0) {
    const setPopulateTrue = (path: string) => {
      url.searchParams.set(path, "true");
    };

    const appendParentPopulate = (fieldName: string | number | symbol) => {
      const mappedParent = mapFieldName(fieldName);
      if (!mappedParent) {
        return null;
      }

      setPopulateTrue(`populate[${mappedParent}]`);
      return mappedParent;
    };

    const appendChildPopulate = (parent: string, child: string | number | symbol) => {
      const mappedChild = mapFieldName(child);
      if (!mappedChild) {
        return;
      }

      setPopulateTrue(`populate[${parent}][populate][${mappedChild}]`);
    };

    populateFields.forEach((descriptor) => {
      if (!descriptor) {
        return;
      }

      if (typeof descriptor === "string") {
        appendParentPopulate(descriptor);
        return;
      }
      if (typeof descriptor === "object") {
        const descriptorMap = descriptor as Record<string, PopulateFieldDescriptor | PopulateFieldDescriptor[] | true>;

        Object.entries(descriptorMap).forEach(([parentField, childValue]) => {
          const mappedParent = mapFieldName(parentField);
          if (!mappedParent) {
            return;
          }

          // Если childValue это true или пустое — просто populate родитель
          if (!childValue || childValue === true) {
            setPopulateTrue(`populate[${mappedParent}]`);
            return;
          }

          // Если есть вложенные поля — НЕ добавляем populate[Parent]=true
          // Только вложенные populate[Parent][populate][Child]=true

          if (typeof childValue === "string") {
            appendChildPopulate(mappedParent, childValue);
            return;
          }

          if (Array.isArray(childValue)) {
            childValue
              .filter((childField): childField is string => typeof childField === "string")
              .forEach((childField) => appendChildPopulate(mappedParent, childField));
          }
        });
      }
    });
  } else {
    // Fallback - загружаем все поля
    if (switchPopulate) {
      url.searchParams.set("populate", "*");
    }
  }

  // Сортировка - только если есть валидные значения
  if (sort && sort.length > 0) {
    url.searchParams.set("sort", sort.join(","));
  }

  // Пагинация - только если числа больше 0
  if (page && page > 0) {
    url.searchParams.set("pagination[page]", page.toString());
  }
  if (pageSize && pageSize > 0) {
    url.searchParams.set("pagination[pageSize]", pageSize.toString());
  }

  if (localFilters) {
    url.searchParams.set("localFilters", JSON.stringify(localFilters));
  }

  if (otherParams) {
    url.searchParams.set("otherParams", JSON.stringify(otherParams));
  }

  // Фильтры - используем новую функцию сериализации
  if (filters && Object.keys(filters).length > 0) {
    serializeStrapiFilters(filters, url);
  }
  if (filtersType === "or" && filters && Object.keys(filters).length > 0) {
    return convertFiltersToOr(url, filters);
  } else {
    return url.toString();
  }
};

// Преобразование Strapi ответа в формат SharePoint
const convertStrapiResponse = <T>(response: StrapiResponse<StrapiItem>): T[] => {
  return response.data.map((item) => {
    // Данные могут быть в item.attributes или на верхнем уровне
    const itemData = item.attributes || item;
    return {
      ID: item.id,
      ...itemData,
    };
  }) as T[];
};

const getFilesByFolder = (data: any[]) => {
  return data.map((item) => {
    // Создаем базовый объект с файловыми полями SharePoint
    if (item.PostImage) {
      item.File = item.PostImage;
    }
    const fileItem = {
      ID: item.id,
      FileRef:
        (item.File && item.File.url.indexOf(s3cloudUrl) !== -1 && item.File.url.replace(s3cloudUrl, siteUrl)) ||
        (item.File && resolveResourceUrl(item.File.url, strapiCheckUrl)) ||
        "",
      // Modification DK
      // FilesGallery: item.Gallery && item.Gallery.length > 0 && item.Gallery.map(file => {
      //   return {
      //     FileRef:
      //       (file && file.url.indexOf(s3cloudUrl) !== -1 && file.url.replace(s3cloudUrl, siteUrl)) ||
      //       (file && resolveResourceUrl(file.url, strapiCheckUrl)) ||
      //       "",
      //     FileLeafRef: file.name || ""
      //   }
      // }) || [],

      FilesGallery:
        (item.Gallery &&
          item.Gallery.length > 0 &&
          item.Gallery.map((file: any) => {
            return {
              FileRef:
                (file && file.url.indexOf(s3cloudUrl) !== -1 && file.url.replace(s3cloudUrl, siteUrl)) ||
                (file && strapiCheckUrl + file.url) ||
                "",
              FileLeafRef: file.name || "",
            };
          })) ||
        [],
      FileLeafRef: item.File?.name || "",
      FileIdInStrapi: item.documentId,
      _ModerationStatus: 0,
      _Level: 1,
      owshiddenversion: 2,
      FSObjType: "0",
      Created_x0020_Date: item.createdAt,
      ProgId: "",
      Modified: (item as any).Modified ?? item.updatedAt ?? null,
      HTML_x0020_File_x0020_Type: null,
      CheckoutUser: null,
      MetaInfo: "",
      Last_x0020_Modified: (item as any).Modified ?? item.updatedAt ?? null,
    };

    // Добавляем все остальные поля из оригинального элемента Strapi
    Object.keys(item).forEach((key) => {
      // Пропускаем поля которые уже обработаны или системные
      if (
        key === "id" ||
        key === "File" ||
        key === "createdAt" ||
        key === "updatedAt" ||
        key === "createdBy" ||
        key === "updatedBy" ||
        key === "publishedAt"
      ) {
        return;
      }
      // Добавляем остальные поля как есть
      (fileItem as any)[key] = item[key];
    });
    return fileItem;
  });
};

export const getStrapiItemsRequest = <K extends ListFieldsKeys>({
  listName,
  filters,
  viewFields,
  sortOrder,
  nextPage,
  rowLimit = 5000,
  filtersType = "and",
  anySite,
  populateFields,
  signal,
  switchPopulate,
  switchViewFields,
  localFilters,
  otherParams,
  method,
  showPrivateCollections = true,
  skipHiddenLocationFilter = false,
  isLocalDate = false,
}: FetchOptions<K>) => {
  try {
    const strapiLN = getListByStrapi(listName);

    if (!strapiLN) {
      throw new Error(`No Strapi mapping found for list: ${listName}`);
    }

    const strapiListName = strapiLN.includes(":folder") ? strapiLN.split(":")[0] : strapiLN;

    // Преобразуем фильтры SharePoint в Strapi формат
    const strapiFilters = convertFiltersToStrapi(filters, listName);

    // Преобразуем сортировку
    const strapiSort = convertSortOrderToStrapi(sortOrder, listName);

    // Преобразуем поля для выборки
    const strapiViewFields = viewFields
      ? viewFields.reduce((acc: string[], field) => {
        if (field) {
          acc.push(field.toString());
        }
        return acc;
      }, [])
      : undefined;

    // Создаем URL
    const url = buildStrapiUrl(
      strapiListName,
      strapiFilters,
      strapiSort,
      nextPage || undefined,
      rowLimit,
      strapiViewFields,
      populateFields,
      filtersType,
      switchPopulate,
      switchViewFields,
      localFilters,
      otherParams,
    );

    return new Promise<{
      items: ListFieldsRecordType[typeof listName][];
      listName: typeof listName;
      nextPage: number | null;
      count: number;
    }>(async (resolve, reject) => {
      try {
        let token: string | null = null;

        if (!isPWA()) {
          const result = await SecureStoragePlugin.get({ key: 'accessToken' });
          token = result.value;
        }

        const headers = {
          Accept: "application/json;odata=nometadata",
          "Content-Type": "application/json;odata=nometadata",
        };

        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
        //Проверка на длину url. Если он пиздец длинный, то нужно писать кастомку на бэк страпи, в остальном остается текущая реализация
        let response;
        if (url.length > USE_POST_THRESHOLD && method) {
          console.log("Слишком большой GET запрос");
          console.log("url: ", url);
          const { baseUrl, params } = transformUrlToNewRoute(url, method);
          response = await axios.post(baseUrl, params, {
            headers,
            withCredentials: true,
            signal,
          });
        } else {
          response = await axios.get(url, {
            method: "GET",
            headers,
            withCredentials: true,
            signal,
          });
        }
        if (response.status !== 200) {
          console.error("Strapi getItems error:", response.status, response.statusText);
          reject(response.statusText);
          throw new Error(`Strapi API error: ${response.status} ${response.statusText}`);
        }

        let data: StrapiResponse<StrapiItem> = response.data;

        // Валидируем данные перед обработкой
        validateStrapiData(listName, data);

        // Обрабатываем файловые списки отдельно
        if (strapiLN.includes(":folder")) {
          const files = getFilesByFolder(data.data);
          // Для файловых списков используем специальную обработку
          // но все равно пропускаем через prepareResponse для корректной обработки полей
          const processedFiles = prepareResponse(
            listName,
            { data: files, meta: data.meta },
            STRAPI_CONFIG.apiUrl,
            isLocalDate
          ) as ListFieldsRecordType[typeof listName][];

          resolve({
            items: processedFiles,
            listName,
            nextPage: data.meta.pagination.page < data.meta.pagination.pageCount ? data.meta.pagination.page + 1 : null,
            count: data.meta.pagination.total,
          });
          return;
        }

        // Используем улучшенную функцию prepareResponse для обработки данных
        const rawItems = prepareResponse(
          listName,
          data,
          STRAPI_CONFIG.apiUrl,
          isLocalDate
        ) as ListFieldsRecordType[typeof listName][];

        const privateFilteredItems = showPrivateCollections ? await filterPrivateCollections(rawItems, listName) : rawItems; // фильтруем приватные коллекции
        const items = await filterHiddenMyTasksLocationData(privateFilteredItems, listName, skipHiddenLocationFilter); // скрываем локацию "Регион" и связанные с ней данные

        // console.log(`Processed items for ${listName}:`, items);

        // Обработка дат (аналогично SharePoint)
        for (let i = 0; i < items.length; i++) {
          Object.keys(items[i]).forEach((key) => {
            if ((key === "Author" || key === "Editor") && !(items[i] as any)[key]) {
              (items[i] as any)[key as any] = {
                get_lookupId: () => 15778,
                get_lookupValue: () => "Supply Portal",
                get_email: () => "supply.portal@abinbevefes.com",
              };
            }
            if ((key === "Created" || key === "Modified") && !(items[i] as any)[key]) {
              (items[i] as any)[key] = new Date();
            }
            if (_.isDate((items[i] as any)[key])) {
              // Здесь можно добавить обработку временных зон если нужно
              // (items[i] as any)[key] = changeTimeZone((items[i] as any)[key], "Europe/Moscow");
            }
          });
        }

        resolve({
          items,
          listName,
          nextPage: data.meta.pagination.page < data.meta.pagination.pageCount ? data.meta.pagination.page + 1 : null,
          count: data.meta.pagination.total,
        });
      } catch (error) {
        console.error("Strapi getItems error:", error);
        reject(error);
      }
    });
  } catch (error) {
    console.error("Strapi request preparation error:", error);
    return Promise.reject(error);
  }
};

// Функция для тестирования и валидации данных Strapi
export const validateStrapiData = (listName: string, data: any) => {
  // console.log(`Validating Strapi data for ${listName}:`, data);

  if (!data || !data.data) {
    console.error("Invalid data structure: missing data property");
    return false;
  }

  if (!Array.isArray(data.data)) {
    console.error("Invalid data structure: data is not an array");
    return false;
  }

  const fields = listFields[listName];
  if (!fields) {
    console.warn(`No field configuration found for list: ${listName}`);
    return true; // Не критично, но стоит проверить
  }

  // Проверяем первые несколько элементов на наличие обязательных полей
  const sampleItems = data.data.slice(0, 3);
  sampleItems.forEach((item: any, index: number) => {
    // Данные из Strapi могут быть в item.attributes или на верхнем уровне
    if (!item.attributes && !item.id) {
      console.warn(`Item ${index} missing required fields:`, item);
    }
  });

  return true;
};

// Функция для получения детальной информации о медиа-файлах
export const getMediaInfo = (mediaData: any) => {
  if (!mediaData || !mediaData.data) return null;

  const media = mediaData.data;

  if (Array.isArray(media)) {
    return media.map((item: any) => ({
      id: item.id,
      url: `${STRAPI_CONFIG.apiUrl.replace("/api/", "")}${item.attributes.url}`,
      name: item.attributes.name,
      alternativeText: item.attributes.alternativeText,
      caption: item.attributes.caption,
      width: item.attributes.width,
      height: item.attributes.height,
      size: item.attributes.size,
      mime: item.attributes.mime,
      formats: item.attributes.formats,
    }));
  }

  return {
    id: media.id,
    url: `${STRAPI_CONFIG.apiUrl.replace("/api/", "")}${media.attributes.url}`,
    name: media.attributes.name,
    alternativeText: media.attributes.alternativeText,
    caption: media.attributes.caption,
    width: media.attributes.width,
    height: media.attributes.height,
    size: media.attributes.size,
    mime: media.attributes.mime,
    formats: media.attributes.formats,
  };
};
